import { useState, useRef, useEffect } from "react";
import { useTypewriter } from '../../hooks/use-type-writer';

import s from "./styles.module.scss";
import CustomDropdown from "../custom-dropdown";
import Button from "../button";
import TaskItem from "../task-item";
import pencilIcon from "../../assets/pencil.svg";
import locationIcon from "../../assets/location.svg";
import clearLogo from "../../assets/clearlogo.svg";
import appleIcon from "../../assets/apple.svg";
import googleIcon from "../../assets/blackgcalicon.svg";
import outlookIcon from "../../assets/blackoutlookicon.svg";
import uploadCloudIcon from "../../assets/uploadCloud.svg";
import trashIcon from "../../assets/trashbucket.svg";
import crossoutIcon from "../../assets/crossout.svg";
import expandIcon from "../../assets/expand.svg";
import minimizeIcon from "../../assets/minimize.svg";



const COLOR_PALETTE = [
  { name: 'Tomato', hex: '#d50000' },
  { name: 'Flamingo', hex: '#e67c73' },
  { name: 'Tangerine', hex: '#f4511e' },
  { name: 'Banana', hex: '#f6bf26' },
  { name: 'Sage', hex: '#33b679' },
  { name: 'Basil', hex: '#0b8043' },
  { name: 'Peacock', hex: '#039be5' },
  { name: 'Blueberry', hex: '#3f51b5' },
  { name: 'Lavender', hex: '#7986cb' },
  { name: 'Grape', hex: '#8e24aa' },
  { name: 'Graphite', hex: '#616161' },
];

const CourseCard = ({
  courseData,
  selectedSection,
  originalSection,
  isExpanded,
  isDeleted,
  hasFile,
  onSectionChange,
  onCourseNameChange,
  onColorChange,
  onCardClick,
  onLocationChange,
  onUpdate,
  onDelete,
  isDescriptionLoading,
  description,
  courseTasks = [],
  tasksLoading = false,
  onTaskClick,
  onTaskToggle,
  onTaskUpdate,
}) => {
  // Detect if section has changed from original
  const sectionChanged = selectedSection && originalSection && (
    selectedSection.name !== originalSection.name ||
    selectedSection.days_of_week !== originalSection.days_of_week ||
    selectedSection.timing !== originalSection.timing
  );
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(courseData.course_name);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [editedLocation, setEditedLocation] = useState(
    selectedSection?.location || courseData.location || "Room TBD"
  );
  const [isTaskFocused, setIsTaskFocused] = useState(false);
  const colorPickerRef = useRef(null);
  const locationInputRef = useRef(null);
  const tasksListRef = useRef(null);

  const currentColor = courseData.color || '#039be5';

  const hexToRgba = (hex, opacity) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setIsColorPickerOpen(false);
      }
    };

    if (isColorPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isColorPickerOpen]);

  useEffect(() => {
    setEditedLocation(selectedSection?.location || courseData.location || "Room TBD");
  }, [selectedSection, courseData.location]);

  // Calculate max-height for task list to show 2 tasks by default
  // Uses ResizeObserver to dynamically adjust when tasks expand/collapse
  useEffect(() => {
    // Only apply when card is expanded but not in task-focused mode
    if (!tasksListRef.current || !isExpanded || isTaskFocused) return;

    let rafId = null;

    const calculateMaxHeight = () => {
      if (!tasksListRef.current) return;

      const taskItems = tasksListRef.current.children;

      if (taskItems.length >= 2) {
        const firstTaskHeight = taskItems[0].offsetHeight;
        const secondTaskHeight = taskItems[1].offsetHeight;
        const gap = 8; // Match CSS gap value
        const padding = 8; // Top and bottom padding
        const maxHeight = firstTaskHeight + secondTaskHeight + gap + padding;
        tasksListRef.current.style.maxHeight = `${maxHeight}px`;
      } else if (taskItems.length === 1) {
        // If only 1 task, set max-height to that task's height
        const taskHeight = taskItems[0].offsetHeight;
        const padding = 8;
        tasksListRef.current.style.maxHeight = `${taskHeight + padding}px`;
      }
    };

    // Initial calculation
    calculateMaxHeight();

    // Use requestAnimationFrame to batch DOM reads/writes and prevent loops
    const rafCalculate = () => {
      // Cancel any pending animation frame
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      // Schedule calculation for next frame
      rafId = requestAnimationFrame(() => {
        calculateMaxHeight();
        rafId = null;
      });
    };

    // Create ResizeObserver to watch for task expansion/collapse
    const resizeObserver = new ResizeObserver(rafCalculate);

    // Observe all child task items
    Array.from(tasksListRef.current.children).forEach(child => {
      resizeObserver.observe(child);
    });

    // Cleanup observer on unmount or when dependencies change
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
    };
  }, [courseTasks, isExpanded, isTaskFocused]);

  const handleCourseNameChange = (e) => {
    setEditedName(e.target.value);
  };

  const handleLocationChange = (e) => {
    setEditedLocation(e.target.value);
  };

  const handleLocationSave = () => {
    if (onLocationChange) {
      onLocationChange(courseData.id, editedLocation);
    }
    setIsEditingLocation(false);
  };

  const handleLocationKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLocationSave();
      e.target.blur();
    } else if (e.key === "Escape") {
      setEditedLocation(selectedSection?.location || courseData.location || "Room TBD");
      setIsEditingLocation(false);
      e.target.blur();
    }
  };

  const handleNameSave = () => {
    onCourseNameChange(courseData.id, editedName);
    setIsEditingName(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleNameSave();
    }
  };

  const handleSectionChange = (selectedOptionText) => {
    const selectedSectionObj = courseData.sections.find(
      (section) => 
        `${section.name} - ${section.days_of_week || "None"} - ${section.timing || "None"}` === selectedOptionText
    );
    if (selectedSectionObj) {
      onSectionChange(courseData.id, selectedSectionObj);
    }
  };

  const handleColorSelect = (color) => {
    if (onColorChange) {
      onColorChange(courseData.id, color.hex);
    }
    setIsColorPickerOpen(false);
  };

  const handleCardClick = (e) => {
    // Don't expand if clicking on interactive elements
    if (
      e.target.tagName === 'INPUT' ||
      e.target.tagName === 'BUTTON' ||
      e.target.tagName === 'SELECT' ||
      e.target.closest('.dropdown') ||
      e.target.closest(`.${s.colorPickerContainer}`)
    ) {
      return;
    }
    onCardClick(courseData.id);
  };

  const { displayedText, isComplete } = useTypewriter(
    description || '',
    20
  );


  // Get current week's date range
  const getCurrentWeekRange = () => {
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 6 = Saturday

    // Get Sunday of current week
    const sunday = new Date(now);
    sunday.setDate(now.getDate() - currentDay);
    sunday.setHours(0, 0, 0, 0);

    // Get Saturday of current week
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);
    saturday.setHours(23, 59, 59, 999);

    return { start: sunday, end: saturday };
  };

  // Format week range for display (e.g., "Nov 3rd-7th")
  const formatWeekRange = () => {
    const { start, end } = getCurrentWeekRange();
    const monthName = start.toLocaleDateString('en-US', { month: 'short' });

    // Add ordinal suffix (st, nd, rd, th)
    const getOrdinal = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };

    const startDay = start.getDate();
    const endDay = end.getDate();

    return `${monthName} ${startDay}${getOrdinal(startDay)}-${endDay}${getOrdinal(endDay)}`;
  };

  // Check if a date is within current week
  const isWithinCurrentWeek = (date) => {
    if (!date) return false;
    const { start, end } = getCurrentWeekRange();
    const checkDate = new Date(date);
    return checkDate >= start && checkDate <= end;
  };


  // Filter and categorize tasks
  const categorizedTasks = {
    thisWeekToDo: courseTasks.filter(task =>
      task.status === 'open' && isWithinCurrentWeek(task.due_at)
    ),
    thisWeekCompleted: courseTasks.filter(task =>
      task.status === 'done' && isWithinCurrentWeek(task.due_at)
    ),
    allOthersToDo: courseTasks.filter(task =>
      task.status === 'open' && !isWithinCurrentWeek(task.due_at)
    ),
    allOthersCompleted: courseTasks.filter(task =>
      task.status === 'done' && !isWithinCurrentWeek(task.due_at)
    ),
  };

  const renderDescription = () => {
    if (isDescriptionLoading) {
      return (
        <div className={s.descriptionLoading}>
          <div className={s.oceanLogoWrapper}>
          <img src={clearLogo} alt="Loading" className={s.oceanLogoSpinner} />
          </div>
          <span className={s.loadingText}>Generating smart insights...</span>
        </div>
      );
    }
    
    if (description) {
      return (
        <p className={s.descriptionText}>
        {displayedText}
        {!isComplete && <span className={s.typingCursor}>|</span>}
      </p>
      );
    }
    
    return (
    <p className={s.descriptionText}>
      <span className={s.fallbackText}>No summary available</span>
    </p>
  );
  };

  // Prepare options for dropdown
  const sectionOptions = courseData.sections.map(
    (section) => `${section.name} - ${section.days_of_week || "None"} - ${section.timing || "None"}`
  );

  // Get current selected value for dropdown
  const currentSelectedValue = selectedSection
    ? `${selectedSection.name} - ${selectedSection.days_of_week || "None"} - ${selectedSection.timing || "None"}`
    : "";

  // Get truncated display value for the dropdown trigger
  const truncatedDisplayValue = selectedSection
    ? `${selectedSection.days_of_week || "None"} - ${selectedSection.timing || "None"}`
    : "";

    // Get display value for collapsed state (just schedule, no section name)
    const collapsedDisplayValue = selectedSection
        ? `${selectedSection.days_of_week || "TBD"}: ${selectedSection.timing || "TBD"}`
        : "No section selected";

  // Get calendar icon based on type
  const getCalendarIcon = (type) => {
    switch (type) {
        case 'google': return googleIcon;
        case 'outlook': return outlookIcon;
        case 'apple': return appleIcon;
        case 'ocean': return clearLogo;
        default: return clearLogo;
    }
  };

  return (
    <div 
      className={`${s.courseCard} ${isExpanded ? s.expanded : ''} ${isDeleted ? s.deleted : ''}`}
      style={{
        //borderLeft: `4px solid ${currentColor}`,
        border: `1px solid ${hexToRgba(currentColor, 1)}`,
        backgroundColor: isExpanded ?  hexToRgba(currentColor, 0.05) : hexToRgba(currentColor, 0.05)
      }}
      onClick={handleCardClick}
    >
      {isDeleted && (
      <img 
        src={crossoutIcon} 
        alt="Deleted" 
        className={isExpanded ? s.crossoutOverlayExpanded : s.crossoutOverlay}
      />
    )}
      <div className={s.cardContent}>
        {/* Collapsed state - read only except color picker */}
        {!isExpanded && (
          <>
            <div className={s.colorPickerWrapper} ref={colorPickerRef}>
              <button 
                className={s.colorButtonTop}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsColorPickerOpen(!isColorPickerOpen);
                }}
                style={{ backgroundColor: currentColor }}
                aria-label="Pick color"
              />
              
              {isColorPickerOpen && (
                <div className={s.colorPaletteTop}>
                  {COLOR_PALETTE.map((color) => (
                    <button
                      key={color.hex}
                      className={s.colorOption}
                      style={{ backgroundColor: color.hex }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleColorSelect(color);
                      }}
                      title={color.name}
                    >
                      {currentColor === color.hex && (
                        <svg width="16" height="16" viewBox="0 0 16 12" fill="white">
                          <path d="M6 11L2 7l1.5-1.5L6 8l5.5-5.5L13 4z"/>
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <div className={s.courseHeaderCollapsed}>
              <h3
                className={`${s.courseName} ${hasFile ? s.updating : ''}`}
                style={{ '--course-color': currentColor }}
              >
                {courseData.course_name}
              </h3>
              {hasFile && (
                <div className={s.uploadLoader}>
                  <div className={s.dots}>
                    <div className={s.dot} style={{ backgroundColor: currentColor }}></div>
                    <div className={s.dot} style={{ backgroundColor: currentColor }}></div>
                    <div className={s.dot} style={{ backgroundColor: currentColor }}></div>
                    <div className={s.dot} style={{ backgroundColor: currentColor }}></div>
                    <div className={s.dot} style={{ backgroundColor: currentColor }}></div>
                  </div>
                  <div className={s.arrow} style={{ color: currentColor }}>↑</div>
                </div>
              )}
            </div>
            
            <div className={s.divider} />
            
            <div className={s.courseInfo}>
              <div className={s.scheduleRow}>
                <span className={s.scheduleText}>{collapsedDisplayValue}</span>
              </div>
              
              <div className={s.locationRow}>
                <img src={locationIcon} alt="Location" className={s.locationIcon} />
                <span className={s.locationText}>
                  {editedLocation}
                </span>
              </div>
              
              <div className={s.smartDescription}>
            {renderDescription()}
              </div>
            </div>
          </>
        )}

        {/* Expanded state - editable */}
        {isExpanded && (
          <>
            <div className={s.courseHeader}>
              <div className={s.colorPickerContainerExpanded} ref={colorPickerRef}>
                <button
                  className={s.colorButtonExpanded}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsColorPickerOpen(!isColorPickerOpen);
                  }}
                  style={{ backgroundColor: currentColor }}
                  aria-label="Pick color"
                />

                {isColorPickerOpen && (
                  <div className={s.colorPaletteExpanded}>
                    {COLOR_PALETTE.map((color) => (
                      <button
                        key={color.hex}
                        className={s.colorOptionExpanded}
                        style={{ backgroundColor: color.hex }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleColorSelect(color);
                        }}
                        title={color.name}
                      >
                        {currentColor === color.hex && (
                          <svg width="16" height="16" viewBox="0 0 16 12" fill="white">
                            <path d="M6 11L2 7l1.5-1.5L6 8l5.5-5.5L13 4z"/>
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className={s.courseTitleContainer}>
                <img
                  src={pencilIcon}
                  alt="Edit"
                  className={s.editIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsEditingName(true);
                  }}
                />
                {isEditingName ? (
                  <input
                    type="text"
                    className={s.courseNameInput}
                    value={editedName}
                    onChange={handleCourseNameChange}
                    onBlur={handleNameSave}
                    onKeyPress={handleKeyPress}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                ) : (
                  <h3 className={s.courseName}>{courseData.course_name}</h3>
                )}
              </div>

              {/* Exit tasks button - only show when in task-focused mode */}
              {isTaskFocused && (
                <button
                  className={s.exitTasksButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsTaskFocused(false);
                  }}
                >
                  Exit tasks list
                  <img src={minimizeIcon} alt="Minimize" className={s.minimizeIcon} />
                </button>
              )}
            </div>
          </>
        )}

        {isExpanded && (
          <div className={s.expandedContent} onClick={(e) => e.stopPropagation()}>
                {/* Tasks Section */}
                {!isTaskFocused ? (
                  <>
                    <div className={s.infoRow}>
                      <div className={s.leftColumn}>
                        <div className={s.sectionSelector} onClick={(e) => e.stopPropagation()}>
                          <CustomDropdown
                            options={sectionOptions}
                            value={currentSelectedValue}
                            onChange={handleSectionChange}
                            placeholder="Select a section"
                            borderColor={currentColor}
                            displayValue={truncatedDisplayValue}
                          />
                        </div>

                        {sectionChanged && !hasFile && (
                          <div className={s.sectionChangeWarning} style={{ color: currentColor }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className={s.infoIcon}>
                              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                              <path d="M12 16V12M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span>Section change detected, please attach your course syllabus below</span>
                          </div>
                        )}

                <div className={s.detailItem}>
                    <img src={locationIcon} alt="Location" className={s.locationIconExpanded} />
                    <div 
                      className={`${s.locationInputWrapper} ${isEditingLocation ? s.editing : ''}`}
                      ref={locationInputRef}
                    >
                      <input
                        type="text"
                        className={s.locationInput}
                        value={editedLocation}
                        onChange={handleLocationChange}
                        onFocus={() => setIsEditingLocation(true)}
                        onBlur={handleLocationSave}
                        onKeyDown={handleLocationKeyPress}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Add location"
                        style={{
                          '--focus-color': currentColor,
                          '--focus-shadow': hexToRgba(currentColor, 0.3)
                        }}
                      />
                    </div>
                    </div>
              
                <div className={s.expandedDescription}>
                    <div className={s.descriptionLabel}>Today's Summary</div>
                    <div className={s.descriptionContent}>
                      {renderDescription()}
                    </div>
                  </div>

                  <div className={s.tasksSection}>
                    <div className={s.tasksLabel}>Tasks</div>

                    {tasksLoading ? (
                      <div className={s.tasksLoading}>Loading tasks...</div>
                    ) : courseTasks.length > 0 ? (
                      <>
                        <div className={s.tasksList} ref={tasksListRef}>
                          {courseTasks.map((task) => (
                            <TaskItem
                              key={task.id}
                              task={task}
                              onToggleStatus={async () => {
                                await onTaskToggle(task);
                              }}
                              onSave={onTaskUpdate}
                              courseColor={currentColor}
                              variant="course"
                            />
                          ))}
                        </div>
                        <button
                          className={s.seeFullTaskListButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsTaskFocused(true);
                          }}
                        >
                          See full task list
                          <img src={expandIcon} alt="Expand" className={s.expandIcon} />
                        </button>
                      </>
                    ) : (
                      <div className={s.noTasksMessage}>No tasks for this course</div>
                    )}
                  </div>
                </div>

                <div className={s.rightColumn}>
                  <div className={s.detailItem}>
                    <img src={getCalendarIcon(courseData.calendar_type)} alt={courseData.calendar_type || 'calendar'} className={s.calendarIcon} />
                    <div className={s.detailText}>
                      <span className={s.detailValue}>
                        {courseData.calendar_type ? courseData.calendar_type.charAt(0).toUpperCase() + courseData.calendar_type.slice(1) : 'Ocean'}
                      </span>
                    </div>
                  </div>

                  {hasFile && (
                    <div className={s.detailItem}>
                      <div className={s.uploadLoader}>
                        <div className={s.dots}>
                          <div className={s.dot} style={{ backgroundColor: currentColor }}></div>
                          <div className={s.dot} style={{ backgroundColor: currentColor }}></div>
                          <div className={s.dot} style={{ backgroundColor: currentColor }}></div>
                          <div className={s.dot} style={{ backgroundColor: currentColor }}></div>
                          <div className={s.dot} style={{ backgroundColor: currentColor }}></div>
                        </div>
                        <div className={s.arrow} style={{ color: currentColor }}>↑</div>
                      </div>
                      <div className={s.detailText}>
                        <span className={s.detailValue} style={{ color: currentColor }}>Updated syllabus attached</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
                  <div className={s.tasksFocusedView}>
                    {/* Tasks header */}
                    <div className={s.tasksFocusedHeader}>Tasks</div>

                    {tasksLoading ? (
                      <div className={s.tasksLoading}>Loading tasks...</div>
                    ) : (
                      <div className={s.tasksColumnsContainer}>
                        {/* Left Column: This Week */}
                        <div className={s.tasksColumn}>
                          <h4 className={s.columnTitle}>This week {formatWeekRange()}</h4>

                          {/* To Do */}
                          <div className={s.tasksCategorySection}>
                            <span className={s.categoryTitle}>To Do:</span>
                            <div className={s.categoryDivider}></div>
                            {categorizedTasks.thisWeekToDo.length > 0 ? (
                              <div className={s.tasksListFocused}>
                                {categorizedTasks.thisWeekToDo.map((task) => (
                                  <TaskItem
                                    key={task.id}
                                    task={task}
                                    onToggleStatus={async () => {
                                      await onTaskToggle(task);
                                    }}
                                    onSave={onTaskUpdate}
                                    courseColor={currentColor}
                                    variant="course"
                                  />
                                ))}
                              </div>
                            ) : (
                              <div className={s.emptyTasksMessage}>No tasks</div>
                            )}
                          </div>

                          {/* Completed */}
                          <div className={s.tasksCategorySection}>
                            <span className={s.categoryTitle}>Completed</span>
                            <div className={s.categoryDivider}></div>
                            {categorizedTasks.thisWeekCompleted.length > 0 ? (
                              <div className={s.tasksListFocused}>
                                {categorizedTasks.thisWeekCompleted.map((task) => (
                                  <TaskItem
                                    key={task.id}
                                    task={task}
                                    onToggleStatus={async () => {
                                      await onTaskToggle(task);
                                    }}
                                    onSave={onTaskUpdate}
                                    courseColor={currentColor}
                                    variant="course"
                                  />
                                ))}
                              </div>
                            ) : (
                              <div className={s.emptyTasksMessage}>No tasks</div>
                            )}
                          </div>
                        </div>

                        {/* Right Column: All Others */}
                        <div className={s.tasksColumn}>
                          <h4 className={s.columnTitle}>All others</h4>

                          {/* To Do */}
                          <div className={s.tasksCategorySection}>
                            <span className={s.categoryTitle}>To Do:</span>
                            <div className={s.categoryDivider}></div>
                            {categorizedTasks.allOthersToDo.length > 0 ? (
                              <div className={s.tasksListFocused}>
                                {categorizedTasks.allOthersToDo.map((task) => (
                                  <TaskItem
                                    key={task.id}
                                    task={task}
                                    onToggleStatus={async () => {
                                      await onTaskToggle(task);
                                    }}
                                    onSave={onTaskUpdate}
                                    courseColor={currentColor}
                                    variant="course"
                                  />
                                ))}
                              </div>
                            ) : (
                              <div className={s.emptyTasksMessage}>No tasks</div>
                            )}
                          </div>

                          {/* Completed */}
                          <div className={s.tasksCategorySection}>
                            <span className={s.categoryTitle}>Completed</span>
                            <div className={s.categoryDivider}></div>
                            {categorizedTasks.allOthersCompleted.length > 0 ? (
                              <div className={s.tasksListFocused}>
                                {categorizedTasks.allOthersCompleted.map((task) => (
                                  <TaskItem
                                    key={task.id}
                                    task={task}
                                    onToggleStatus={async () => {
                                      await onTaskToggle(task);
                                    }}
                                    onSave={onTaskUpdate}
                                    courseColor={currentColor}
                                    variant="course"
                                  />
                                ))}
                              </div>
                            ) : (
                              <div className={s.emptyTasksMessage}>No tasks</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className={s.actionButtons}>
            <Button
                variant="secondary"
                size="small"
                icon={<img src={uploadCloudIcon} alt="Upload" />}
                className={sectionChanged && !hasFile ? s.animatedBorderButton : ''}
                onClick={(e) => {
                    e.stopPropagation();
                    onUpdate(courseData.id);
                }}
                style={{
                    '--hover-color': hexToRgba(currentColor, 0.1),
                    '--hover-border': currentColor,
                    '--border-color': currentColor
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = hexToRgba(currentColor, 0.1);
                    e.currentTarget.style.borderColor = currentColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '';
                    e.currentTarget.style.borderColor = '';
                  }}
                >
                {sectionChanged && !hasFile ? 'Upload' : 'Update'}
            </Button>
            <Button
                variant={isDeleted ? "secondary" : "error"}
                size="small"
                icon={<img src={trashIcon} alt="Delete" />}
                onClick={(e) => {
                    e.stopPropagation();
                    onDelete(courseData.id);
                }}
            >
            Delete
        </Button>
        </div>
        <div className={s.lastUpdated}>
                <span className={s.lastUpdatedLabel}>LAST UPDATED </span>
                <span className={s.lastUpdatedValue}> 
                    {courseData.updated_at 
                      ? new Date(courseData.updated_at).toLocaleDateString()
                      : "Never"}
                  </span>
                </div>
              </div>
        )}
      </div>
    </div>
  );
};

export default CourseCard;